import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactElement } from 'react';
import { cn } from '../util/classes.ts';

export const toggleSwitchVariants = cva(
    [
        'appearance-none shrink-0 cursor-pointer',
        'h-5 w-9 rounded-full bg-content-weaker',
        'transition-[background-color,background-position,background-size] duration-interactive ease-interactive',
        // thumb rendered as a background SVG that slides between sides
        'bg-no-repeat bg-size-[16px_16px] bg-position-[2px_center]',
        'bg-(image:--toggle-switch-thumb)',
        'hover:bg-content-weak',
        'focus-visible:outline-1 focus-visible:outline-(--color-main) focus-visible:outline-offset-1',
        'state-disabled:pointer-events-none state-disabled:opacity-disabled',
        'active:translate-px',
        // checked: track filled with --color-main, thumb slides to the right
        'state-checked:bg-(--color-main)',
        'state-checked:bg-(image:--toggle-switch-thumb)',
        'state-checked:bg-position-[calc(100%-2px)_center]',
        'state-checked:hover:bg-[color-mix(in_oklch,var(--color-main)_var(--tint-hover),var(--tint-base))]',
        // indeterminate: track stays unchecked color, thumb squashed and centered
        'state-indeterminate:bg-center indeterminate:bg-size-[8px_8px]',
    ],
    {
        variants: {
            variant: {
                default: ['[--color-main:var(--color-content)]'],
                primary: ['[--color-main:var(--color-brand)]'],
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

type ToggleSwitchVariantProps = VariantProps<typeof toggleSwitchVariants>;

export type ToggleSwitchProps = ComponentPropsWithRef<'input'> & ToggleSwitchVariantProps;

export function ToggleSwitch({ className, variant, ...props }: ToggleSwitchProps): ReactElement {
    return (
        <input
            data-slot="switch"
            type="checkbox"
            role="switch"
            {...props}
            className={cn(toggleSwitchVariants({ variant }), className)}
        />
    );
}
